import { useEffect, useRef } from 'react';
import { css } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { useImagePreviewActions } from '../provider';

const bodyContainerStyle = css`
  display: flex;
  width: 100%;
  justify-content: center;
  gap: ${theme.spacing(5)};
  align-items: center;
  max-height: 70dvh;
`;

export interface ImagePreviewBodyProps {
  children: React.ReactNode;
}

export const ImagePreviewBody = ({ children }: ImagePreviewBodyProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { setBodyDimensions } = useImagePreviewActions();

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let frame;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const { width, height } = entry.contentRect;

      cancelAnimationFrame(frame);

      frame = requestAnimationFrame(() => {
        setBodyDimensions((prev) => {
          if (prev.width === width && prev.height === height) return prev;
          return { width, height };
        });
      });
    });

    observer.observe(element);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={ref} className={bodyContainerStyle}>
      {children}
    </div>
  );
};

ImagePreviewBody.displayName = 'ImagePreviewBody';
